'use client';

/**
 * The mailbox switch for one agent (migration 0080), mounted at the top of the
 * Requests section on components/agent-details.tsx.
 *
 * WHY THIS IS A SWITCH AND NOT A FIELD. Every other control in that panel edits
 * a column on the agent row and is written by the panel's Save. This one edits
 * `agent_addresses.status` through its own route, so it saves IMMEDIATELY and
 * never calls `markDirty`. Folding it into Save would mean a person could tick
 * "receive email", navigate away, and be left believing their agent is
 * reachable when nothing was written.
 *
 * OFF IS THE DEFAULT AND OFF IS THE TRUTH. A fresh agent's address row is
 * minted `inactive`: the address is reserved so nobody else can take the
 * spelling, and nothing routes to it. So this component shows the address in
 * BOTH states -- an owner deciding whether to switch the mailbox on wants to
 * see the address they would be publishing, not a blank until after they
 * commit.
 *
 * 503 IS NOT AN ERROR TO APOLOGISE FOR. On an environment where migration 0079
 * has not synced, the route answers `mailbox_unavailable` and the switch stays
 * off and disabled. That is the accurate state of that environment, so it is
 * rendered as a plain note rather than a failure.
 */

import { useCallback, useEffect, useState } from 'react';
import { Mail } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { Spinner } from '@/components/ui/spinner';

export interface MailboxState {
  enabled: boolean;
  canonical: string | null;
  vanity: string | null;
}

/**
 * `onEnabledChange` reports the switch's state to the panel whenever it is
 * known or changes (2026-10-08): the email-replies control beside it is about
 * answering email, so it shows only while the mailbox is on. The dev pass
 * found it offered with the switch off, a setting for a door that was shut.
 */
export function AgentMailboxToggle({ agentId, onEnabledChange }: { agentId: string; onEnabledChange?: (enabled: boolean) => void }) {
  const t = useT();
  const [state, setState] = useState<MailboxState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enabled = state?.enabled ?? false;
  useEffect(() => {
    if (!loading) onEnabledChange?.(enabled);
  }, [enabled, loading, onEnabledChange]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/mailbox`, { cache: 'no-store' });
        if (cancelled) return;
        if (res.ok) setState(await res.json());
        else setUnavailable(true);
      } catch {
        if (!cancelled) setUnavailable(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agentId]);

  const toggle = useCallback(async (next: boolean) => {
    setSaving(true);
    setError(null);
    // Optimistic, because the switch should feel immediate; reverted below on
    // any non-2xx so the control never lies about what the server stored.
    const previous = state;
    setState((s) => (s ? { ...s, enabled: next } : s));
    try {
      const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/mailbox`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      if (res.ok) {
        setState(await res.json());
      } else {
        setState(previous);
        if (res.status === 503) setUnavailable(true);
        else setError(t('agents.mailboxSaveFailed'));
      }
    } catch {
      setState(previous);
      setError(t('agents.mailboxSaveFailed'));
    } finally {
      setSaving(false);
    }
  }, [agentId, state, t]);

  if (loading) {
    return <div className="flex justify-center py-3"><Spinner /></div>;
  }

  const address = state?.vanity ?? state?.canonical ?? null;
  const on = state?.enabled ?? false;
  const disabled = unavailable || saving || !state;

  return (
    <div className="space-y-3">
      {/* A SWITCH, not a checkbox, and the same one the consent control below
          it uses. The section had one of each: a `<input type=checkbox>` here
          and a `role="switch"` for shared conversations, three rows apart in a
          panel narrow enough to see both at once. The two controls do the same
          kind of thing (turn a capability on) so they look the same. */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-medium flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
            {t('agents.mailboxEnable')}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">{t('agents.mailboxEnableDesc')}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={t('agents.mailboxEnable')}
          disabled={disabled}
          onClick={() => void toggle(!on)}
          /* h-6 w-10 and translate-x-5, matching the twelve other switches in
             this panel rather than the 44px this briefly was. */
          className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${on ? 'bg-primary' : 'bg-muted-foreground/30 ring-1 ring-border'}`}
        >
          {/* `bg-white`, NOT `bg-background`. The token follows the theme, so in
              dark mode the knob resolved to rgb(10,10,10) on a rgb(74,74,74)
              pill: 2.2:1, against 8.9:1 for every other switch in the panel,
              and the ON state read as less active than its neighbours' OFF.
              A switch knob is a physical object, not a surface; it stays white
              in both themes. Caught by a browser pass, 2026-09-09. */}
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </button>
      </div>

      {/* The address is shown in BOTH states (file comment): someone deciding
          whether to switch the mailbox on wants to see what they would be
          publishing. It gets its own labelled field once on, because at that
          point it is a thing to copy and give out rather than a footnote. */}
      {address && (
        <div className={`rounded-lg border border-border p-3 ${on ? '' : 'opacity-60'}`}>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">{t('agents.mailboxAddressLabel')}</p>
          <p className="text-xs font-mono break-all mt-1">{address}</p>
        </div>
      )}
      {unavailable && <p className="text-xs text-muted-foreground">{t('agents.mailboxUnavailable')}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
